import { useState } from 'react';
import { Table } from '../ui/Table';
import { Button } from '../ui/Button';
import { DropdownMenu } from '../ui/DropdownMenu';
import { Pagination } from '../ui/Pagination';
import { 
  Check, 
  Eye, 
  Edit2, 
  Trash2, 
  CheckSquare, 
  MoreVertical, 
  FileText, 
  Download,
  Upload,
  Paperclip,
  AlertTriangle
} from 'lucide-react';
import { JOURNAL_TYPES } from '../../config/accounting';
import type { JournalEntry } from '../../types/accounting';
import { Switch } from '../ui/Switch';
import { useCurrency } from '../../hooks/useCurrency';

interface UnifiedJournalListProps {
  entries: JournalEntry[];
  agentEntries: JournalEntry[];
  onView: (entry: JournalEntry) => void;
  onEdit: (entry: JournalEntry) => void;
  onDelete: (entry: JournalEntry) => void;
  onValidate?: (entry: JournalEntry) => void;
  onValidateAll?: (entries: JournalEntry[]) => void;
  onExport: (format: 'pdf' | 'excel' | 'csv') => void;
  onUploadAttachment?: (entry: JournalEntry, file: File) => void;
  onDownloadAttachment?: (entry: JournalEntry, attachmentId: string) => void;
  loading?: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function UnifiedJournalList({
  entries,
  agentEntries,
  onView,
  onEdit,
  onDelete,
  onValidate,
  onValidateAll,
  onExport,
  onUploadAttachment,
  onDownloadAttachment,
  loading,
  page,
  onPageChange
}: UnifiedJournalListProps) {
  const [selectedEntries, setSelectedEntries] = useState<JournalEntry[]>([]);
  const [showAgentEntries, setShowAgentEntries] = useState(true);
  const itemsPerPage = 10;
  
  // Combine entries and agent entries for unified display
  const allEntries = [...entries];
  if (showAgentEntries) {
    allEntries.push(...agentEntries);
  }
  
  // Pagination des données
  const startIndex = (page - 1) * itemsPerPage;
  const paginatedEntries = allEntries.slice(startIndex, startIndex + itemsPerPage);
  const actualTotalPages = Math.ceil(allEntries.length / itemsPerPage);
  
  const isAllSelected = agentEntries.length > 0 && selectedEntries.length === agentEntries.length;
  const { formatConverted } = useCurrency();
  
  const toggleSelectAll = () => {
    if (isAllSelected) {
      setSelectedEntries([]);
    } else {
      setSelectedEntries([...agentEntries]);
    }
  };

  const toggleSelectEntry = (entry: JournalEntry) => {
    if (selectedEntries.find(e => e.id === entry.id)) {
      setSelectedEntries(selectedEntries.filter(e => e.id !== entry.id));
    } else {
      setSelectedEntries([...selectedEntries, entry]);
    }
  };

  const getRowClassName = (entry: JournalEntry) => {
    if (entry.source === 'agent' && entry.validationStatus === 'pending') {
      return 'bg-orange-50 hover:bg-orange-100';
    }
    
    if (entry.status === 'posted') {
      return 'bg-gray-50 hover:bg-gray-100';
    }
    
    if (entry.status === 'approved') {
      return 'bg-green-50 hover:bg-green-100';
    }
    
    return '';
  };

  const columns = [
    {
      header: 'Date',
      accessor: (entry: JournalEntry) => new Date(entry.date).toLocaleDateString('fr-FR')
    },
    {
      header: 'N° Pièce',
      accessor: (entry: JournalEntry) => entry.reference
    },
    {
      header: 'Journal',
      accessor: (entry: JournalEntry) => {
        // Mapping between journalType and JOURNAL_TYPES constant keys
        const journalTypeMapping: { [key: string]: keyof typeof JOURNAL_TYPES } = {
          'sales': 'SALES',
          'purchases': 'PURCHASE',
          'bank': 'BANK',
          'cash': 'CASH',
          'general': 'GENERAL'
        };
        
        const mappedType = journalTypeMapping[entry.journalType];
        return mappedType ? JOURNAL_TYPES[mappedType].label : entry.journalType;
      }
    },
    {
      header: 'Comptes',
      accessor: (entry: JournalEntry) => (
        <div className="text-sm">
          {entry.lines.map((line) => (
            <div key={line.id} className="flex items-center gap-2 mb-1">
              <span className="font-mono text-xs bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                {line.accountCode}
              </span>
              <span className="text-gray-600 dark:text-gray-400 truncate max-w-32">
                {line.accountName}
              </span>
            </div>
          ))}
        </div>
      )
    },
    {
      header: 'Libellé',
      accessor: (entry: JournalEntry) => (
        <div className="flex items-center">
          {entry.source === 'agent' && (
            <span className="inline-flex items-center rounded-full bg-orange-100 px-2 py-1 text-xs font-medium text-orange-800 mr-2">
              ADHA
            </span>
          )}
          <span className="truncate">{entry.description}</span>
        </div>
      )
    },
    {
      header: 'Débit',
      accessor: (entry: JournalEntry) => formatConverted(entry.totalDebit),
      className: 'text-right'
    },
    {
      header: 'Crédit',
      accessor: (entry: JournalEntry) => formatConverted(entry.totalCredit),
      className: 'text-right'
    },
    {
      header: 'Pièces',
      accessor: (entry: JournalEntry) => (
        <div className="flex items-center gap-2">
          {entry.attachments && entry.attachments.length > 0 ? (
            <div className="flex items-center gap-1">
              <Paperclip className="h-4 w-4 text-green-600" />
              <span className="text-xs text-green-600">{entry.attachments.length}</span>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
              <span className="text-xs text-orange-600">0</span>
            </div>
          )}
        </div>
      )
    },
    {
      header: 'Actions',
      accessor: (entry: JournalEntry) => {
        const isPosted = entry.status === 'posted';
        const isAgentPending = entry.source === 'agent' && entry.validationStatus === 'pending';
        
        const menuItems = [
          {
            label: 'Voir les détails',
            icon: Eye,
            onClick: () => onView(entry)
          },
          ...(entry.attachments && entry.attachments.length > 0 ? [{
            label: 'Télécharger pièces',
            icon: Download,
            onClick: () => entry.attachments?.forEach(att => 
              onDownloadAttachment && onDownloadAttachment(entry, att.id)
            )
          }] : []),
          ...(!isPosted && onUploadAttachment ? [{
            label: 'Ajouter pièce',
            icon: Upload,
            onClick: () => {
              const input = document.createElement('input');
              input.type = 'file';
              input.accept = '.pdf,.jpg,.jpeg,.png';
              input.onchange = (e) => {
                const file = (e.target as HTMLInputElement).files?.[0];
                if (file) onUploadAttachment(entry, file);
              };
              input.click();
            }
          }] : []),
          ...(!isPosted ? [{
            label: 'Modifier',
            icon: Edit2,
            onClick: () => onEdit(entry)
          }] : []),
          ...(isAgentPending && onValidate ? [{
            label: 'Valider',
            icon: Check,
            onClick: () => onValidate(entry)
          }] : []),
          ...(!isPosted ? [{
            label: 'Supprimer',
            icon: Trash2,
            onClick: () => onDelete(entry),
            variant: 'danger' as const
          }] : [])
        ];

        return (
          <DropdownMenu
            trigger={
              <Button
                variant="ghost"
                size="sm"
                icon={MoreVertical}
                className="h-8 w-8 p-0"
              />
            }
            items={menuItems}
          />
        );
      }
    }
  ];
  // Si des entrées d'agent sont sélectionnées, ajouter une colonne de sélection
  if (agentEntries.length > 0 && showAgentEntries) {
    columns.unshift({
      header: 'Sélection',
      accessor: (entry: JournalEntry) => (
        entry.source === 'agent' && entry.validationStatus === 'pending' ? (
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={Boolean(selectedEntries.find(e => e.id === entry.id))}
              onChange={() => toggleSelectEntry(entry)}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
          </div>
        ) : null
      ),
      className: 'w-10'
    } as any); // Type assertion to any to avoid TypeScript errors
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Switch
              checked={showAgentEntries}
              onCheckedChange={setShowAgentEntries}
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Afficher les entrées ADHA ({agentEntries.length})
            </span>
          </div>
          {selectedEntries.length > 0 && onValidateAll && (
            <Button
              variant="primary"
              size="sm"
              icon={CheckSquare}
              onClick={() => onValidateAll(selectedEntries)}
            >
              Valider la sélection ({selectedEntries.length})
            </Button>
          )}
        </div>
        
        <div className="flex items-center space-x-2">
          <span className="text-sm text-gray-500">
            {allEntries.length} écriture{allEntries.length > 1 ? 's' : ''}
          </span>
          <DropdownMenu
            trigger={
              <Button variant="secondary" icon={Download}>
                Exporter
              </Button>
            }
            items={[
              {
                label: 'Export PDF',
                icon: FileText,
                onClick: () => onExport('pdf')
              },
              {
                label: 'Export Excel',
                icon: FileText,
                onClick: () => onExport('excel')
              },
              {
                label: 'Export CSV',
                icon: Download,
                onClick: () => onExport('csv')
              }
            ]}
          />
        </div>
      </div>

      {showAgentEntries && agentEntries.length > 0 && (
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            checked={isAllSelected}
            onChange={toggleSelectAll}
            className="mr-2 rounded border-gray-300 text-primary focus:ring-primary"
            id="select-all"
          />
          <label htmlFor="select-all" className="text-sm">
            Tout sélectionner ({agentEntries.length} écritures ADHA)
          </label>
        </div>
      )}

      <Table
        columns={columns}
        data={paginatedEntries}
        loading={loading}
        emptyMessage="Aucune écriture trouvée. Utilisez les filtres pour affiner votre recherche ou créez une nouvelle écriture."
        emptyTitle="Aucune écriture comptable"
        emptyAction={{
          label: "Créer une écriture",
          onClick: () => console.log("Créer une nouvelle écriture") // À connecter avec la vraie action
        }}
        rowClassName={getRowClassName}
      />
      
      {actualTotalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={actualTotalPages}
          onPageChange={onPageChange}
        />
      )}
    </div>
  );
}
